/**
 * Model UI utility functions
 * React component utilities for rendering model badges and icons
 */

import { Badge, Text, Tooltip } from '@mantine/core';
import { IconEye, IconWorld, IconPalette, IconBulb } from '@tabler/icons-react';
import type { ModelInfo } from '@gofranz/gpt-muse-common';
import { getModelBadgeData, getModelIconData } from './model-utils';

const getIconComponent = (iconName: string, size: number) => {
  switch (iconName) {
    case 'eye':
      return <IconEye size={size} />;
    case 'world':
      return <IconWorld size={size} />;
    case 'palette':
      return <IconPalette size={size} />;
    case 'bulb':
      return <IconBulb size={size} />;
    default:
      return null;
  }
};

/**
 * Get capability badges for a model as JSX elements
 * @param model - The model to get badges for
 * @returns Array of React elements representing badges
 */
export const getModelBadges = (model: ModelInfo) => {
  const badgeData = getModelBadgeData(model);

  return badgeData.map((badge) => (
    <Badge key={badge.key} size="sm" color={badge.color} variant="light">
      {badge.icon && (
        <>
          {getIconComponent(badge.icon, 12)}
          <span style={{ marginLeft: 2 }}>{badge.label}</span>
        </>
      )}
      {!badge.icon && badge.label}
    </Badge>
  ));
};

/**
 * Get capability icons for compact display (tooltips) as JSX elements
 * @param model - The model to get icons for
 * @returns Array of React elements representing icons with tooltips
 */
export const getModelIcons = (model: ModelInfo) => {
  const iconData = getModelIconData(model);

  return iconData.map((icon) => (
    <Tooltip key={icon.key} label={icon.label} position="top" withArrow>
      {icon.type === 'text' ? (
        <Text span style={{ color: icon.color, fontSize: '12px', fontWeight: 'bold' }}>
          {icon.text}
        </Text>
      ) : (
        <span style={{ color: icon.color }}>
          {getIconComponent(icon.iconName!, 12)}
        </span>
      )}
    </Tooltip>
  ));
};